import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { resolvePodPhoto } from "@/components/PodNetworkSection";

export default function Locations() {
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [distanceMode, setDistanceMode] = useState("off"); // off | requesting | on

  useEffect(() => { load(); }, []);

  const load = async (lat, lng) => {
    setLoading(true);
    try {
      const params = lat != null && lng != null ? `?lat=${lat}&lng=${lng}` : "";
      const { data } = await api.get(`/pods${params}`);
      setPods(data.items || []);
    } finally { setLoading(false); }
  };

  const requestDistance = () => {
    if (!("geolocation" in navigator)) return;
    setDistanceMode("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setDistanceMode("on"); load(pos.coords.latitude, pos.coords.longitude); },
      () => setDistanceMode("off"),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const live = pods.filter((p) => p.status === "active");
  const upcoming = pods.filter((p) => p.status !== "active");

  return (
    <div className="min-h-screen bg-white" data-testid="locations-page">
      <Navbar />

      {/* HERO */}
      <section className="bg-brand-blue text-white py-16 md:py-24 border-b-8 border-brand-red">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-3">Our Locations</p>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tightest leading-none">
            Rent from your <span className="text-brand-gold italic">neighborhood.</span>
          </h1>
          <p className="text-slate-200 text-sm md:text-base font-bold mt-5 max-w-2xl leading-relaxed">
            Every Party Rentals Express pod is a 24/7 self-serve rental warehouse — commercial-grade gear you unlock with a PIN. Here&apos;s where you can rent today, and where we&apos;re headed next.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={requestDistance}
              disabled={distanceMode !== "off"}
              className="bg-brand-gold text-brand-blue px-5 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-amber-300 disabled:opacity-70"
              data-testid="locations-distance-btn"
            >
              <i className="fas fa-location-arrow mr-2" />
              {distanceMode === "requesting" ? "Finding you…" : distanceMode === "on" ? "Sorted by distance" : "Show nearest to me"}
            </button>
          </div>
        </div>
      </section>

      {/* LIVE */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red">Open Now</p>
            <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">Rent Today</h2>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{live.length} pod{live.length === 1 ? "" : "s"} live</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest" data-testid="locations-loading">
            <i className="fas fa-spinner fa-spin mr-2" /> Loading locations…
          </div>
        ) : live.length === 0 ? (
          <div className="text-center py-16 text-slate-400 font-bold" data-testid="locations-live-empty">
            No live pods yet — check back soon.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locations-live-grid">
            {live.map((p) => <BigPodCard key={p.id} pod={p} />)}
          </div>
        )}
      </section>

      {/* UPCOMING */}
      {upcoming.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-8 pb-20">
          <div className="mb-8">
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold">Opening Soon</p>
            <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tightest text-brand-blue">Coming Next</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locations-upcoming-grid">
            {upcoming.map((p) => <BigPodCard key={p.id} pod={p} upcoming />)}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-brand-slate py-16 border-t border-slate-100">
        <div className="max-w-3xl mx-auto text-center px-4">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">Not Near a Pod Yet?</p>
          <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tightest text-brand-blue mb-4">Tell us where to build the next one.</h3>
          <p className="text-slate-500 text-sm font-bold mb-6">
            Join a pod waitlist above, or email <a className="text-brand-red hover:underline" href="mailto:hello@partyrentalsexpress.com">hello@partyrentalsexpress.com</a> with your neighborhood.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function BigPodCard({ pod, upcoming = false }) {
  return (
    <Link
      to={`/pods/${pod.slug}`}
      className="group block bg-white rounded-[28px] overflow-hidden border border-slate-100 hover:border-brand-gold hover:shadow-2xl transition-all"
      data-testid={`location-card-${pod.slug}`}
    >
      <div className="relative h-56 overflow-hidden bg-slate-200">
        <img src={resolvePodPhoto(pod.photo_url)} alt={pod.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.currentTarget.style.opacity = "0.4"; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 flex gap-2">
          {upcoming ? (
            <span className="bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-[0.3em] px-3 py-1.5 rounded-full shadow">
              <i className="fas fa-hourglass-half mr-1.5" /> Opening Soon
            </span>
          ) : (
            <span className="bg-emerald-500 text-white text-[9px] font-black uppercase tracking-[0.3em] px-3 py-1.5 rounded-full shadow">
              <i className="fas fa-circle text-[6px] mr-1.5 animate-pulse" /> Live Now
            </span>
          )}
        </div>
        {pod.distance_miles != null && (
          <div className="absolute top-4 right-4 bg-white/95 backdrop-blur text-brand-blue text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-full shadow">
            <i className="fas fa-location-arrow mr-1.5" /> {pod.distance_miles} mi
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-1">{pod.neighborhood}</p>
          <h3 className="text-2xl font-black uppercase tracking-tightest text-white leading-none">{pod.name}</h3>
        </div>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-slate-500 text-xs font-bold flex items-start gap-2">
          <i className="fas fa-map-marker-alt text-brand-red mt-0.5" />
          <span>{pod.address}</span>
        </p>
        <p className="text-slate-500 text-xs font-bold flex items-center gap-2">
          <i className="fas fa-clock text-brand-blue" /> {pod.hours || "24/7 self-serve"}
        </p>
        {pod.tagline && <p className="text-slate-600 text-sm font-bold leading-relaxed line-clamp-2">{pod.tagline}</p>}
        <div className="pt-2 flex items-center justify-between border-t border-slate-100">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Explore Pod</span>
          <span className={`text-[10px] font-black uppercase tracking-widest ${upcoming ? "text-brand-blue" : "text-brand-red"} group-hover:translate-x-1 transition-transform`}>
            {upcoming ? "Join Waitlist" : "Rent Now"} <i className="fas fa-arrow-right ml-1.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
